// combatpro/dice/DiceControls.js
//
// Combat PRO - DiceControls (Stage 36 correction: legacy Quick Roll markup).
//
// Reproduces the Quick Roll card from battletracker.html (the read-only master
// spec, dice-block template) with its ORIGINAL inline layout structure, not a
// re-designed semantic layout. React ownership replaces the old DOM
// manipulation; it does not redesign the view. The grid positions, inline
// styles, button face text, selection label, and Clear / Roll Custom buttons
// match the source.
//
// Source model (unchanged, accepted behaviour): clicking a die adds one of that
// die to the current selection; clicking again adds more (d4 d6 d8 d10 d12 d20
// d100); the label summarises the selection; Clear empties it; Roll Custom rolls
// the accumulated dice. Selected buttons take the source's selected styling
// (background #3498db, white text, #1a6fa8 border) exactly as vi_selectDie
// applied it; unselected buttons keep the source's #ddd / #333 / #aaa styling.
//
// Single global runtime: the source's per-monster id suffixes (dice-selector-
// ${index}, dice-selection-label-${index}) are reproduced WITHOUT the index
// suffix (dice-selector, dice-selection-label), matching the Stage 36 global
// dice runtime decision. data-sides and data-count are preserved.
//
// Presentational only: receives selection + handler props, calls them. No dice
// mathematics, no state ownership, no storage, no API, no AI, no direct DOM
// access. No optional chaining. No em dash.
//
// Button face text matches the source btn.textContent: "{count}x d{sides}" when
// count > 1 (the source multiplication sign reproduced as {'\u00d7'}), else
// "d{sides}".

import React from 'react';

var DICE_BUTTONS = [
  { sides: 4, gridColumn: '1', gridRow: '1' },
  { sides: 6, gridColumn: '2', gridRow: '1' },
  { sides: 8, gridColumn: '3', gridRow: '1' },
  { sides: 10, gridColumn: '1', gridRow: '2' },
  { sides: 12, gridColumn: '2', gridRow: '2' },
  { sides: 20, gridColumn: '3', gridRow: '2' },
  { sides: 100, gridColumn: '4', gridRow: '1 / span 2' }
];

var DICE_SIDES = [4, 6, 8, 10, 12, 20, 100];

function DiceControls(props) {
  var selection = props.selection;

  function faceText(sides) {
    var count = selection[sides] || 0;
    if (count > 1) {
      return count + '' + '\u00d7' + ' d' + sides;
    }
    return 'd' + sides;
  }

  function selectionLabel() {
    var parts = [];
    for (var i = 0; i < DICE_SIDES.length; i++) {
      var sides = DICE_SIDES[i];
      var count = selection[sides] || 0;
      if (count > 0) { parts.push(count + 'd' + sides); }
    }
    return parts.length ? 'Rolling: ' + parts.join(' + ') : 'Nothing selected';
  }

  function buttonStyle(btn) {
    var isActive = (selection[btn.sides] || 0) > 0;
    var base = {
      gridColumn: btn.gridColumn,
      gridRow: btn.gridRow,
      width: '100%',
      minHeight: '48px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '1.2',
      position: 'relative'
    };
    if (btn.sides === 100) {
      base.height = '100%';
    }
    if (isActive) {
      base.background = 'rgba(96,165,250,0.22)';
      base.color = '#BFDBFE';
      base.border = '2px solid rgba(96,165,250,0.6)';
    } else {
      base.background = 'var(--cp-surface-2)';
      base.color = 'var(--cp-text)';
      base.border = '1px solid var(--cp-border-strong)';
    }
    return base;
  }

  return (
      <div className="vi-dice-card" style={{ padding: '16px', background: 'var(--cp-surface)', borderRadius: '12px', border: '1px solid var(--cp-border)', display: 'flex', flexDirection: 'column', height: 'auto', justifyContent: 'flex-start', gap: '12px' }}>
        <h4 style={{ margin: '0 0 12px 0', color: 'var(--cp-gold-bright)', fontSize: '20px', fontWeight: 600 }}>Quick Roll {'\u2014'} click to select, click again to add more</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr) 1fr', gridTemplateRows: 'repeat(2,1fr)', gap: '12px' }} id="dice-selector">
          {DICE_BUTTONS.map(function (btn) {
            return (
              <button
                key={btn.sides}
                type="button"
                data-sides={btn.sides}
                data-count={selection[btn.sides] || 0}
                style={buttonStyle(btn)}
                onClick={function () { props.onSelectDie(btn.sides); }}
              >{faceText(btn.sides)}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div id="dice-selection-label" style={{ flex: 1, fontSize: '14px', color: 'var(--cp-text-muted)', fontStyle: 'italic' }}>{selectionLabel()}</div>
          <button type="button" style={{ padding: '8px 14px', background: 'var(--cp-surface-2)', color: 'var(--cp-text-2)', border: '1px solid var(--cp-border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }} onClick={function () { props.onClearSelection(); }}>Clear</button>
          <button type="button" style={{ padding: '11px 22px', background: 'linear-gradient(180deg,#FFCE4E,#F0B429)', color: '#0B0F14', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 800, fontSize: '15px', whiteSpace: 'nowrap' }} onClick={function () { props.onRollSelected(); }}>🎲 Roll Custom</button>
        </div>
      </div>
  );
}

export default DiceControls;
